module Meevo.Plugins.MeevoMessenger {

    import IMeevoMessengerSettings = Meevo.Model.IMeevoMessengerSettings;
    import Employee = Meevo.Model.Employee;
    import ClientListItem = Meevo.Model.ClientListItem;
    import BusinessHours = Meevo.Model.BusinessHours;
    import Holiday = Meevo.Model.Holiday;


    export interface IMeevoMessengerApiService {
        createConversation: (conversationSettings: MeevoMessengerConversation) => ng.IPromise<MeevoMessengerConversation>;
        getSettings: (classification: string) => ng.IPromise<IMeevoMessengerSettings>;
        saveSettings: (settings: IMeevoMessengerSettings) => ng.IPromise<IMeevoMessengerSettings>;
        sendMessage: (userPhone: string, message: string, conversationId: Guid, classification: MeevoMessengerClassificationApiType, isClient: boolean, userId?: string) => ng.IPromise<any>;
        getConversationList: (reqBody: IConversationBodyRequest) => ng.IPromise<Array<MeevoMessengerConversation>>;
        getConversationMessages: (bodyReq: any) => ng.IPromise<Array<MeevoMessengerMessage>>;
        updateConversation: (conversation: MeevoMessengerConversation) => ng.IPromise<MeevoMessengerConversation>;
        undeleteConversation: (conversation: MeevoMessengerConversation) => ng.IPromise<MeevoMessengerConversation>
        transferMessages: (messages: MeevoMessengerMessage[]) => ng.IPromise<any>;
        getSystemAutoReply: (classification: string) => ng.IPromise<string>;
        getLocationTime(): ng.IPromise<string>;
        getBusinessHours: () => ng.IPromise<BusinessHours[]>;
        getAttachmentsByExternalMsgId: (externalMessageId: string, classification: string) => ng.IPromise<Meevo.Model.IMeevoMessengerAttachmentItem[]>;
        checkHolidays: (locationTime: string) => ng.IPromise<IHolidaysStatusForCurrentDays>;
        getClientsList: (filteringOption: string) => ng.IPromise<ClientListItem[]>;
        getClientById: (id: Guid) => ng.IPromise<Meevo.Model.Client>;
        getEmployeesList: (filteringOption: string) => ng.IPromise<Employee[]>;
        externalMarkMessagesAsRead: (arrayOfMessages: Array<Guid>, classification: string) => ng.IPromise<any>;
        isArrivalsEnabled: () => ng.IPromise<boolean>;
        getAppointmentStatus: (clientIds: Guid[] ) => ng.IPromise<IArrivalsAppointmentStatus[]>;
        getAppointmentData: (clientIds: Guid[]) => ng.IPromise<IAppointmentDataInfo[]>;
        isClientOptedOut: (data: IOptedOutDataRequest) => ng.IPromise<boolean>;
        getBusinessInfo(): ng.IPromise<any>;
        getEmployeeById(id: Guid): ng.IPromise<any>;
        checkMeevoAIProfanity:(message: string) => ng.IPromise<IProfanityResponse>;
        checkNumberEligibility: (phoneNumber: string) => ng.IPromise<any>;
    }

    export class MeevoMessengerApiService implements IMeevoMessengerApiService {

        static $inject = [
            'meevo.components.common.DataService',
            'meevo.AppSettings',
            'meevo.components.common.IdentityService'
        ];

        constructor(
            public dataService: Meevo.Components.BaseClasses.IDataService,
            public appSettings: Meevo.Components.Common.IAppSettings,
            public identityService: Meevo.Components.Common.IIdentityService
        ) { }

        /**
         * Create new conversation with the client or employee
         * @param {MeevoMessengerConversation} conversationSettings
         * @returns {ng.IPromise<MeevoMessengerConversation>} promise
         */
        createConversation(conversationSettings: MeevoMessengerConversation): ng.IPromise<MeevoMessengerConversation> {
            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.meevoMessenger}/Conversation/Save`, conversationSettings);
        }

        /**
         * Get Meevo Messenger Settings
         * @param {string} classification
         * @returns {ng.IPromise<IMeevoMessengerSettings>} promise
         */
        getSettings(classification: string): ng.IPromise<IMeevoMessengerSettings> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/meevomessenger/Setting?classification=${classification}`, 'GET', null)
                .then(response => response.data);
        }

        /**
         * Save Meevo Messenger Settings
         * @param {IMeevoMessengerSettings} settings
         * @returns {ng.IPromise<IMeevoMessengerSettings>} promise
         */
        saveSettings(settings: IMeevoMessengerSettings): ng.IPromise<IMeevoMessengerSettings> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Setting`, 'POST', settings)
                .then(response => response.data);
        }

        /**
         * Send a message to specific phone number
         * @param {string} userPhone
         * @param {string} message
         * @param {Guid} conversationId
         * @param {string} classification
         * @param {boolean} isClient
         * @param {Guid} userId
         * @returns {ng.IPromise<any>} promise
         */
        sendMessage(userPhone: string, message: string, conversationId: Guid, classification: MeevoMessengerClassificationApiType, isClient?: boolean, userId?: string): ng.IPromise<any> {
            const body: SendMessageBody = {
                userPhone,
                message,
                conversationId,
                classification,
            };

            if (userId) {
                body[isClient ? 'clientId' : 'employeeId'] = userId;
            }

            return this.dataService.add(`${this.appSettings.API_ENDPOINTS.meevoMessenger}/Message/Send`, body);
        }

        /**
         * Get Client or Employees Conversation List
         * @param {IConversationBodyRequest} reqBody
         * @returns {ng.IPromise<Array<MeevoMessengerConversation>>} promise
         */
        getConversationList(reqBody: IConversationBodyRequest): ng.IPromise<Array<MeevoMessengerConversation>> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Conversation/List`, 'POST', reqBody)
                .then(response => response.data);
        }

        /**
         * Get messages for conversation
         * @param {any} bodyReq
         * @returns {ng.IPromise<Array<MeevoMessengerMessage>>} promise
         */
        getConversationMessages(bodyReq: any): ng.IPromise<Array<MeevoMessengerMessage>> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Message/List`, 'POST', bodyReq)
                .then(response => response.data);
        }

        /**
         * Update conversation
         * @param {MeevoMessengerConversation} conversation
         * @returns { ng.IPromise<MeevoMessengerConversation>} promise
         */
        updateConversation(conversation: MeevoMessengerConversation): ng.IPromise<MeevoMessengerConversation> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Conversation/Update`, 'PUT', conversation)
                .then(response => response.data);
        }

        /**
         * Undelete conversation
         * @param { MeevoMessengerConversation } conversation
         * @returns { ng.IPromise<MeevoMessengerConversation>} promise
         */
        undeleteConversation(conversation: MeevoMessengerConversation): ng.IPromise<MeevoMessengerConversation> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Conversation/undelete`, 'PUT', {
                conversationId: conversation.id,
                entityVersion: conversation.entityVersion,
                clientId: conversation.clientId,
                employeeId: conversation.employeeId,
                phoneNumber: conversation.phoneNumber,
                classification: conversation.classification
            }).then(response => response.data);
        }

        /**
         * Transfer messages to other conversation
         * @param {MeevoMessengerMessage[]} messages
         * @returns {ng.IPromise<any>} promise
         */
        transferMessages(messages: MeevoMessengerMessage[]): ng.IPromise<any> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Message/Bulk`, 'POST', { messages })
                .then(response => response.data);
        }

        /**
         * Get system auto-reply for outside hours
         * @param {string} classification
         * @returns {ng.IPromise<string>} promise
         */
        getSystemAutoReply(classification: string): ng.IPromise<string> {
            let params = {
                classification: classification
            };
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Setting/AutoReply`, 'GET', {}, params)
                .then(result => result.data.currentAutoReplyMessage);
        }

        /**
         * Get real location time fot logic related to real time changing of auto-reply block
         * @returns {ng.IPromise<string>}
         */
        getLocationTime(): ng.IPromise<string> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/TimeClock/LocationTime`, 'GET', null)
                .then(result => result.data.locationTime);
        }

        /**
         * Get Filtered Employees by filtering option
         * @param {string} filteringOption
         * @returns {ng.IPromise<ClientListItem[]>} promise
         */
        getClientsList(filteringOption: string): ng.IPromise<ClientListItem[]> {
            const requestBody = {
                criteria: { isGlobal: false, filter: filteringOption },
                fullPhoneNumber: filteringOption,
                offset: 0,
                limit: 100
            };
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/${this.appSettings.API_ENDPOINTS.client}/List`, 'POST', requestBody)
                .then(response => response.data);
        }

        /**
         * Get Client by Id
         * @param {Guid} id
         * @returns {ng.IPromise<Meevo.Model.Client>} promise
         */
        getClientById(id: Guid): ng.IPromise<Meevo.Model.Client> {
            return this.dataService.getItem(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.client}`, id)
                .then(response => <Meevo.Model.Client>response);
        }

        /**
         * Get Filtered Employees by filtering option
         * @param {string} filteringOption
         * @returns {ng.IPromise<Employee[]>} promise
         */
        getEmployeesList(filteringOption: string): ng.IPromise<Employee[]> {
            const requestBody = {
                criteria: { isGlobal: false, showEmployeesOnly: true, includeFullAccess: true, filter: filteringOption },
                fullPhoneNumber: filteringOption,
                offset: 0,
                limit: 100
            };
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/${this.appSettings.API_ENDPOINTS.employee}/List`, 'POST', requestBody)
                .then(response => response.data);
        }

        /**
         * Get business hours
         * @returns {ng.IPromise<BusinessHours[]>} promise
         */
        getBusinessHours(): ng.IPromise<BusinessHours[]> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.businessInfo}`, 'GET', null)
                .then(response => response.data.businessHours);
        }

        /**
         * Check if current day is holiday
         * @param {string} locationTime
         * @returns {ng.IPromise<IHolidaysStatusForCurrentDays>} promise
         */
        checkHolidays(locationTime: string): ng.IPromise<IHolidaysStatusForCurrentDays> {
            let holidayUrl;

            // Textel doesn't work in CO but let it be
            if (this.identityService.inCentralOfficeMode() && (this.identityService.userBusinessTypes.length > 0)) {
                holidayUrl = this.appSettings.API_ENDPOINTS.holiday + '/' + this.identityService.userBusinessTypes[0].id;
            } else {
                holidayUrl = this.appSettings.API_ENDPOINTS.holiday + '/' + this.identityService.businessTypeId;
            }

            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${holidayUrl}`, 'GET', null)
                .then(result => result.data.holidays)
                .then((holidaysList: Holiday[]) => {
                    let isCurrentDayHoliday = false;
                    let isNextDayHoliday = false;

                    for (const holiday of holidaysList) {
                        const preparedHoliday = moment(holiday.holidayDate).startOf('day');

                        if (!isCurrentDayHoliday) isCurrentDayHoliday = preparedHoliday.isSame(moment(locationTime).startOf('day'));
                        if (!isNextDayHoliday) isNextDayHoliday = preparedHoliday.isSame(moment(locationTime).startOf('day').add(1, 'day'));

                        if (isCurrentDayHoliday && isNextDayHoliday) break;
                    }

                    return { isCurrentDayHoliday, isNextDayHoliday };
                });
        }

        /**
         * Mark unread message as read
         * @param {Array<Guid>} arrayOfMessages
         * @param {string} classification
         * @returns {ng.IPromise<any>} promise
         */
        externalMarkMessagesAsRead(arrayOfMessages: Array<Guid>, classification: string): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/Message/MarkAsRead`, 'POST', { messageIds: arrayOfMessages, classification })
                .then(response => response.data);
        }

        /**
         * Get message's attachments by its external message id
         * @param {string} externalMessageId
		 * @param {string} classification
         * @returns {ng.IPromise<Meevo.Model.IMeevoMessengerAttachmentItem[]>}
         */
		getAttachmentsByExternalMsgId(externalMessageId: string, classification: string): ng.IPromise<Meevo.Model.IMeevoMessengerAttachmentItem[]> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.textel}/MessageAttachments`, 'POST', { externalMessageId, classification })
                .then(response => response.data);
        }
        /**
         * Get Appointment Status by client id
         * @param {Guid[]} clientIds
         * @returns {ng.IPromise<Meevo.Model.IArrivalsAppointmentStatus[]>}
         */
        getAppointmentStatus(clientIds: Guid[] ): ng.IPromise<IArrivalsAppointmentStatus[]> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/AppointmentStatuses`, 'POST', {clientIds} )
                .then(response => response.data);
        }

        /**
         * checked if arrivals enabled for meevo messenge
         * @returns {ng.IPromise<boolean>}
         */
        isArrivalsEnabled(): ng.IPromise<boolean> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.notificationSetting}`, 'GET', null)
                .then(response => response.data)
                .then(notificationSettings => {
                    const arrivalsConfig: any = _.findWhere(notificationSettings.communicationTypeNotifications, { communicationType: Meevo.Model.CommunicationTypeEnum.AppointmentCheckIn });
                    return arrivalsConfig && arrivalsConfig.isSMSEnabled;
                });
        }


        /*
        * get appointment data by clients ids
        * * @param {Guid[]} clientIds
        * @returns {ng.IPromise<any[]>}
         */
        getAppointmentData(clientIds: Guid[]): ng.IPromise<IAppointmentDataInfo[]> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/AppointmentData`, 'POST', {clientIds})
                .then(response => response.data)
        }

        /*
        * get is client Opted Out
        * @param {IOptedOutDataRequest} requestData
        * @returns {ng.IPromise<boolean>}
         */
        isClientOptedOut(requestData: IOptedOutDataRequest): ng.IPromise<boolean> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/IsOptedOut`, 'POST', requestData )
                .then(response => response.data)
        }

        getBusinessInfo(): ng.IPromise<any> {
            return this.dataService.customMethodData('api/tenant/businessinfo/', 'get', {}).then((data) => {
                return data
            });
        }
        /*
                * get employee by id
                * @param {Guid} id
                * @returns {ng.IPromise<Employee>}
                 */
        getEmployeeById(id: Guid): ng.IPromise<Employee> {
            return this.dataService.getItem('employee', id)
                .then((employee: Employee) => employee)
        }

        /*
        * check message for profanity words
        * @param {string} message
        * @returns {ng.IPromise<IProfanityResponse>}
         */
        checkMeevoAIProfanity(message: string): ng.IPromise<IProfanityResponse> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.meevoMessenger}/message/check`, 'POST', { message })
                .then((profanityResponse: any) => profanityResponse.data)
        }

        checkNumberEligibility(phoneNumber: string): ng.IPromise<any> {
            return this.dataService.customMethodData(`${this.appSettings.API_ROOT}/HostedNumber/validate`, 'POST', { phoneNumber })
                .then(response => response.data);
        }
    }

    angular.module('meevo.plugins.meevoMessenger')
        .service('meevo.plugins.meevoMessenger.meevoMessengerApiService', MeevoMessengerApiService);
}
